import React, { useEffect, useState, useMemo } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Spinner,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonDataTable from "../../common/DataTable";
import { fetchDrawingsHistory } from "../../slices/thunks";
import moment from "moment";

const DrawingsHistory = () => {
    const dispatch = useDispatch();

    const [filterParams, setFilterParams] = useState({
        state: "",
        from: "", // No date filter by default
        to: "", // No date filter by default
        page: 1,
        perPage: 10,
    });

    const drawingsHistory = useSelector((state) => state.DrawingsHistory?.drawingsHistory || {});
    const pagination = useSelector((state) => state.DrawingsHistory?.pagination || {});
    const loading = useSelector((state) => state.DrawingsHistory?.loading || false);

    const [availableStates, setAvailableStates] = useState([
        "Georgia",
        "New York",
        "Florida",
        "New Jersey",
        "Anguila",
        "California",
        "Chicago",
        "Connecticut",
        "Delaware",
        "Idaho",
        "Indiana",
        "Kentucky",
        "Maryland",
        "Pennsylvania",
        "North Carolina",
        "Arizona",
        "Arkansas",
        "Colorado",
        "District of Columbia",
        "Iowa",
        "Kansas",
        "Louisiana",
        "Maine",
        "Massachusetts",
        "Michigan",
        "Mississippi",
        "Minnesota",
        "Missouri",
        "Nebraska",
        "New Hampshire",
        "Ohio",
        "Oklahoma",
        "Oregon",
        "Rhode Island",
        "South Carolina",
        "South Dakota",
        "Tennessee",
        "Texas",
        "Vermont",
        "Virginia",
        "Washington",
        "West Virginia",
        "Wisconsin",
        "Wyoming"
    ]);

    useEffect(() => {
        if (drawingsHistory) {
            const fetchedStates = Object.keys(drawingsHistory);
            setAvailableStates((prev) => {
                const newStates = fetchedStates.filter((state) => !prev.includes(state));
                return newStates.length > 0 ? [...prev, ...newStates] : prev;
            });
        }
    }, [drawingsHistory]);

    const statesChoices = useMemo(() => {
        // Sort states alphabetically
        const sortedStates = [...availableStates].sort((a, b) => a.localeCompare(b));
        return [
            { label: "All States", value: "" },
            ...sortedStates.map((state) => ({ label: state, value: state })),
        ];
    }, [availableStates]);

    const fetchData = (params = {}) => {
        const updated = { ...filterParams, ...params };
        setFilterParams(updated);
        dispatch(fetchDrawingsHistory(updated));
    };

    useEffect(() => {
        fetchData();
    }, []);

    const flattenedData = useMemo(() => {
        const flattened = [];
        Object.entries(drawingsHistory).forEach(([state, times]) => {
            Object.entries(times).forEach(([time, dates]) => {
                Object.entries(dates).forEach(([date, games]) => {
                    if (Array.isArray(games)) {
                        games.forEach((game, index) => {
                            flattened.push({
                                id: `${state}-${time}-${date}-${game.game}-${index}`,
                                state,
                                time,
                                date,
                                ...game,
                            });
                        });
                    }
                });
            });
        });
        return flattened;
    }, [drawingsHistory]);

    const columns = [
        {
            name: <span className="font-weight-bold fs-13">Date</span>,
            selector: (row) => row.date,
            sortable: true,
            cell: (row) => row.date ? moment(row.date).format("DD-MM-YYYY") : "-",
            width: "15%"
        },
        {
            name: <span className="font-weight-bold fs-13">Time</span>,
            selector: (row) => row.time,
            sortable: true,
            width: "10%"
        },
        {
            name: <span className="font-weight-bold fs-13">Game</span>,
            selector: (row) => row.game,
            sortable: true,
            width: "10%"
        },
        {
            name: <span className="font-weight-bold fs-13">Numbers</span>,
            width: "20%",
            cell: (row) => {
                const numbers = Array.isArray(row.numbers)
                    ? row.numbers
                    : (typeof row.numbers === 'string' ? row.numbers.split(',') : []);

                return (
                    <div className="d-flex gap-1 flex-wrap">
                        {numbers.length > 0 ? numbers.map((num, i) => (
                            <span
                                key={i}
                                className="badge bg-primary-subtle text-primary fs-12 px-2 py-1 rounded-circle"
                                style={{ minWidth: '24px', minHeight: '24px', textAlign: 'center', alignContent: 'center', display: 'inline-block' }}
                            >
                                {num}
                            </span>
                        )) : <span className="text-muted">-</span>}
                    </div>
                );
            },
        },
        {
            name: <span className="font-weight-bold fs-13">State</span>,
            selector: (row) => row.state,
            sortable: true,
            width: "10%"
        },
        {
            name: <span className="font-weight-bold fs-13">Source</span>,
            selector: (row) => row.source,
            sortable: true,
            width: "20%"
        },
        {
            name: <span className="font-weight-bold fs-13">Fetched At</span>,
            selector: (row) => row.fetchedAt,
            sortable: true,
            cell: (row) => row.fetchedAt ? moment(row.fetchedAt).format("YYYY-MM-DD HH:mm:ss") : "-",
            width: "15%"
        },
    ];

    const handleStateChange = (selectedOption) => {
        if (selectedOption) {
            fetchData({ state: selectedOption.value, page: 1 });
        }
    };

    const handleDateChange = (dates) => {
        if (dates.length === 2) {
            fetchData({
                from: moment(dates[0]).format("YYYY-MM-DD"),
                to: moment(dates[1]).format("YYYY-MM-DD"),
                page: 1,
            });
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Drawings History" pageTitle="Drawings" />
                    <CommonDataTable
                        columns={columns}
                        data={flattenedData}
                        totalRows={pagination.totalData}
                        loading={loading}
                        fetchData={fetchData}
                        filterParams={filterParams}
                        paginationEnabled
                        customfwrappClassName="w-100 gap-0"
                        customfmainClassName="w-100 flex-1"
                        customFilters={
                            <div className="d-flex gap-3 py-2 align-items-center justify-content-between w-100" style={{ flex: '0 1 0%' }}>
                                <div style={{ minWidth: '200px' }}>
                                    <Select
                                        value={statesChoices.find(s => s.value === filterParams.state)}
                                        options={statesChoices}
                                        onChange={handleStateChange}
                                        className="basic-single"
                                        classNamePrefix="select"
                                        placeholder="Select State"
                                    />
                                </div>
                                <div style={{ minWidth: '250px' }}>
                                    <Flatpickr
                                        className="form-control d-block bg-white"
                                        options={{
                                            mode: "range",
                                            dateFormat: "Y-m-d",
                                            defaultDate: filterParams.from && filterParams.to ? [filterParams.from, filterParams.to] : null,
                                        }}
                                        onChange={handleDateChange}
                                        placeholder="All Dates"
                                    />
                                </div>
                                {loading && <Spinner size="sm" color="primary" />}
                            </div>
                        }
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default DrawingsHistory;
